/**
 * Manga List Context
 * Manages manga data state (parallel to AniListContext for anime).
 * Shares authentication from AniListContext.
 * Uses hybrid pre-computed clusters for group building (same as anime).
 */

import React, { createContext, useState, useContext, useCallback, useEffect, useRef } from 'react';
import { useAniList } from './AniListContext';
import { fetchUserMangaList } from '../services/aniListService';
import { batchFetchLatestChapters } from '../services/mangaUpdatesService';
import { EXCLUDED_DISPLAY_RELATION_TYPES, RELATION_TYPE_INVERSE } from '../config/constants';
import { useTitlePreference } from './TitlePreferenceContext';
import { getDisplayTitle } from '../hooks/useDisplayTitle';

const MangaListContext = createContext();

// Excluded relation types for pending manga detection (uses shared constant)
const filterPendingManga = (pending) => {
    if (!pending || pending.length === 0) return pending;
    return pending.filter(m => !EXCLUDED_DISPLAY_RELATION_TYPES.has(m.relationType));
};

export const MangaListProvider = ({ children }) => {
    const { isConnected, username, token } = useAniList();

    // Core manga state
    const [mangaUserData, setMangaUserData] = useState(null);
    const [mangaGroups, setMangaGroups] = useState([]);
    const [pendingManga, setPendingManga] = useState([]);
    const [mangaRelationsData, setMangaRelationsData] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [isMangaReady, setIsMangaReady] = useState(false);
    const [latestChapterMap, setLatestChapterMap] = useState({});
    const [pendingChapterMap, setPendingChapterMap] = useState({});
    const muFetchInProgress = useRef(false);
    const pendingMuFetchInProgress = useRef(false);
    const buildLockRef = useRef(false);
    const { titlePreference } = useTitlePreference();

    // No localStorage cache needed — clusters are pre-computed on server
    useEffect(() => {
        if (!isConnected) {
            setIsLoading(false);
            return;
        }
        setIsLoading(false);
    }, [isConnected]);

    /**
     * Build manga groups from pre-computed server clusters
     * Same approach as AniListContext.buildGroupsFromClusters
     */
    const buildGroupsFromData = useCallback(async (userData) => {
        if (!userData || !userData.lists) return;
        if (buildLockRef.current) return;

        buildLockRef.current = true;

        try {
            const allEntries = userData.lists.flatMap(l => l.entries);
            if (allEntries.length === 0) {
                buildLockRef.current = false;
                return;
            }

            const mediaIds = allEntries.map(entry => entry.media?.id).filter(Boolean);
            // titlePreference from reactive hook (was localStorage)

            console.log(`📖 Building manga groups from clusters for ${mediaIds.length} entries...`);

            // Fetch pre-computed clusters from server
            const { getClustersForManga } = await import('../services/apiService');
            let result;
            try {
                result = await getClustersForManga(mediaIds);
            } catch (clusterErr) {
                console.warn('⚠️ Manga cluster fetch failed:', clusterErr.message);
            }

            if (!result || !result.clusters) {
                // Fallback: use old server-side group builder
                console.warn('⚠️ Manga cluster fetch failed, falling back to server group build');
                try {
                    const { buildMangaGroupsOnServer } = await import('../services/apiService');

                    // Extract embedded relations
                    const relationsMap = {};
                    allEntries.forEach(entry => {
                        if (entry.media?.relations?.edges) {
                            relationsMap[entry.media.id] = {
                                id: entry.media.id,
                                relations: {
                                    edges: entry.media.relations.edges.map(edge => ({
                                        ...edge,
                                        node: { ...edge.node }
                                    }))
                                }
                            };
                        }
                    });

                    const serverResult = await buildMangaGroupsOnServer(allEntries, relationsMap);
                    if (serverResult && serverResult.groups) {
                        setMangaGroups(serverResult.groups);
                        setPendingManga(filterPendingManga(serverResult.pending || []));
                        setMangaRelationsData(relationsMap);
                        setIsMangaReady(true);

                        // Fetch chapters in background
                        fetchLatestChaptersBackground(allEntries);
                        fetchPendingChaptersBackground(serverResult.pending || []);

                        return { groups: serverResult.groups, pending: serverResult.pending || [] };
                    }
                } catch (fallbackErr) {
                    console.warn('⚠️ Server manga group build failed:', fallbackErr.message);
                }
                return;
            }

            // Build entry lookup map
            const entryMap = {};
            allEntries.forEach(entry => {
                if (entry.media?.id) entryMap[entry.media.id] = entry;
            });

            const userIdSet = new Set(mediaIds);
            const { getDisplayTitle, calculateGroupStats, sortGroupByReleaseDate } = await import('../services/groupingService');

            // Convert clusters to the group format the UI expects
            const groups = [];
            const pendingMangaList = [];
            const relData = {};

            for (const [groupId, cluster] of Object.entries(result.clusters)) {
                const { animeIds, catalogData, relations } = cluster;

                const userMangaInCluster = animeIds.filter(id => userIdSet.has(id));
                const nonUserMangaInCluster = animeIds.filter(id => !userIdSet.has(id));

                if (userMangaInCluster.length === 0) continue;

                const groupEntries = userMangaInCluster
                    .map(id => entryMap[id])
                    .filter(Boolean);

                if (groupEntries.length === 0) continue;

                const sorted = sortGroupByReleaseDate(groupEntries);
                const stats = calculateGroupStats(sorted);
                const groupName = getDisplayTitle(sorted[0].media, titlePreference);

                groups.push({
                    id: `cluster-${groupId}`,
                    name: groupName,
                    anime: sorted, // Note: groupingService uses 'anime' key for both anime and manga
                    stats,
                    firstReleaseDate: sorted[0]?.media?.startDate || null,
                    status: sorted.every(e => e.status === 'COMPLETED') ? 'COMPLETED' : 'IN_PROGRESS'
                });

                // Detect pending manga (related manga not in user's list)
                for (const nonUserId of nonUserMangaInCluster) {
                    const catData = catalogData?.[nonUserId];
                    if (!catData) continue;

                    let relationType = 'RELATED'; // safe default
                    let sourceAnimeId = null;

                    // Check 1 (preferred): relations FROM user manga TO pending manga (correct perspective)
                    for (const userId of userMangaInCluster) {
                        if (relations?.[userId]) {
                            const edges = relations[userId]?.relations?.edges || [];
                            for (const edge of edges) {
                                if (edge.node?.id === nonUserId) {
                                    relationType = edge.relationType;
                                    sourceAnimeId = userId;
                                    break;
                                }
                            }
                        }
                        if (sourceAnimeId) break;
                    }

                    // Check 2 (fallback): relations FROM pending manga TO user manga (inverted)
                    if (!sourceAnimeId) {
                        if (relations?.[nonUserId]) {
                            const edges = relations[nonUserId]?.relations?.edges || [];
                            for (const edge of edges) {
                                if (userIdSet.has(edge.node?.id)) {
                                    relationType = RELATION_TYPE_INVERSE[edge.relationType] || edge.relationType;
                                    sourceAnimeId = edge.node.id;
                                    break;
                                }
                            }
                        }
                    }

                    if (EXCLUDED_DISPLAY_RELATION_TYPES.has(relationType)) continue;

                    // Skip orphaned manga: no direct non-excluded connection found
                    if (relationType === 'RELATED' && !sourceAnimeId) continue;

                    const sourceEntry = sourceAnimeId ? entryMap[sourceAnimeId] : null;

                    pendingMangaList.push({
                        id: nonUserId,
                        title: catData.title,
                        type: catData.type,
                        format: catData.format,
                        status: catData.status,
                        episodes: catData.episodes,
                        chapters: catData.chapters,
                        volumes: catData.volumes,
                        startDate: catData.startDate,
                        coverImage: catData.coverImage,
                        averageScore: catData.averageScore,
                        description: catData.description,
                        relationType,
                        relatedTo: sourceEntry?.media ? {
                            id: sourceEntry.media.id,
                            title: getDisplayTitle(sourceEntry.media, titlePreference)
                        } : null,
                        sourceAnimeId,
                        clusterGroupId: groupId
                    });
                }

                if (relations) {
                    Object.assign(relData, relations);
                }
            }

            // Deduplicate pending
            const seenPending = new Set();
            const uniquePending = pendingMangaList.filter(p => {
                if (seenPending.has(p.id)) return false;
                seenPending.add(p.id);
                return true;
            });

            const filteredPending = filterPendingManga(uniquePending);

            setMangaGroups(groups);
            setPendingManga(filteredPending);
            setMangaRelationsData(relData);
            setIsMangaReady(true);

            console.log(`✅ Manga cluster-based build complete: ${groups.length} groups, ${filteredPending.length} pending from ${mediaIds.length} manga`);

            // Fetch chapters in background
            fetchLatestChaptersBackground(allEntries);
            fetchPendingChaptersBackground(filteredPending);

            return { groups, pending: filteredPending };
        } catch (err) {
            console.error('❌ Error building manga groups from clusters:', err);
        } finally {
            buildLockRef.current = false;
        }
    }, []);

    /**
     * Background fetch of latest chapter data from MangaUpdates
     */
    const fetchLatestChaptersBackground = useCallback(async (entries) => {
        if (muFetchInProgress.current) return;
        if (!entries || entries.length === 0) return;

        muFetchInProgress.current = true;
        try {
            console.log('📖 Starting background MangaUpdates fetch...');
            const chapterMap = await batchFetchLatestChapters(entries);
            setLatestChapterMap(chapterMap);
            console.log(`📖 MangaUpdates: Cached ${Object.keys(chapterMap).length} entries`);
        } catch (error) {
            console.warn('⚠️ MangaUpdates background fetch failed:', error.message);
        } finally {
            muFetchInProgress.current = false;
        }
    }, []);

    /**
     * Background fetch of latest chapter data for pending (related) manga
     */
    const fetchPendingChaptersBackground = useCallback(async (pendingList) => {
        if (pendingMuFetchInProgress.current) return;
        if (!pendingList || pendingList.length === 0) return;

        const needsFetch = pendingList.filter(m =>
            !m.chapters && ['RELEASING', 'HIATUS', null, undefined].includes(m.status)
        );
        if (needsFetch.length === 0) return;

        pendingMuFetchInProgress.current = true;
        try {
            console.log(`📖 Fetching chapter data for ${needsFetch.length} pending manga...`);
            const wrappedEntries = needsFetch.map(m => ({ media: m }));
            const chapterMap = await batchFetchLatestChapters(wrappedEntries);
            setPendingChapterMap(chapterMap);
            console.log(`📖 Pending manga chapters: ${Object.keys(chapterMap).length} entries`);
        } catch (error) {
            console.warn('⚠️ Pending manga chapter fetch failed:', error.message);
        } finally {
            pendingMuFetchInProgress.current = false;
        }
    }, []);

    /**
     * Fetch and build manga data from AniList
     */
    const fetchMangaData = useCallback(async (forceRefresh = false) => {
        if (!isConnected || !username) return;
        if (isLoading) return;

        const hasCachedData = mangaGroups.length > 0;
        if (!hasCachedData) {
            setIsLoading(true);
        }

        try {
            console.log('📖 Fetching manga list...');
            const data = await fetchUserMangaList(username, token);

            if (data) {
                setMangaUserData(data);
                await buildGroupsFromData(data);

                setIsMangaReady(true);
                console.log('📖 Manga data loaded successfully');

            }
        } catch (error) {
            console.warn('⚠️ Failed to fetch manga data:', error.message);
            if (hasCachedData) {
                console.log('📖 Using cached manga data (fetch failed)');
            }
        } finally {
            setIsLoading(false);
        }
    }, [isConnected, username, token, isLoading, mangaGroups.length, buildGroupsFromData]);

    /**
     * Update manga data directly from sync service (no extra API call)
     */
    const updateMangaDataDirect = useCallback(async (syncData, skipSave = false) => {
        if (!syncData || !syncData.lists) return;
        setMangaUserData(syncData);
        await buildGroupsFromData(syncData);
        setIsMangaReady(true);
    }, [buildGroupsFromData]);

    /**
     * Refresh manga data (force fetch from API)
     */
    const refreshMangaData = useCallback(async () => {
        await fetchMangaData(true);
    }, [fetchMangaData]);

    // Auto-fetch manga data when connected — only if no data loaded yet
    useEffect(() => {
        if (isConnected && !mangaUserData && !isMangaReady && !isLoading) {
            console.log('📊 No cached manga data (first-time user) — fetching from API...');
            fetchMangaData();
        }
    }, [isConnected, mangaUserData, isMangaReady, isLoading, fetchMangaData]);

    // Derived reactive manga groups/pending (instant toggle)
    const displayedMangaGroups = React.useMemo(() => {
        if (!mangaGroups || mangaGroups.length === 0) return mangaGroups;
        return mangaGroups.map(group => {
            const media = (group.anime && group.anime[0]?.media) || (group.manga && group.manga[0]?.media);
            if (!media) return group;
            const newName = getDisplayTitle(media, titlePreference);
            return newName === group.name ? group : { ...group, name: newName };
        });
    }, [mangaGroups, titlePreference]);
    const displayedPendingManga = React.useMemo(() => {
        if (!pendingManga || pendingManga.length === 0) return pendingManga;
        const mediaMap = new Map();
        if (mangaUserData?.lists) mangaUserData.lists.flatMap(l => l.entries || []).forEach(e => { if (e.media?.id) mediaMap.set(e.media.id, e.media); });
        mangaGroups.forEach(g => { (g.anime || g.manga || []).forEach(entry => { if (entry.media?.id) mediaMap.set(entry.media.id, entry.media); }); });
        return pendingManga.map(p => {
            if (!p.relatedTo || p.sourceAnimeId == null) return p;
            const srcMedia = mediaMap.get(p.sourceAnimeId) || mediaMap.get(p.relatedTo.id);
            if (!srcMedia) return p;
            const nt = getDisplayTitle(srcMedia, titlePreference);
            if (nt === p.relatedTo.title) return p;
            return { ...p, relatedTo: { ...p.relatedTo, title: nt } };
        });
    }, [pendingManga, mangaGroups, mangaUserData, titlePreference]);
    const value = {
        // Data
        mangaUserData,
        mangaGroups: displayedMangaGroups,
        pendingManga: displayedPendingManga,
        mangaRelationsData,
        latestChapterMap,
        pendingChapterMap,
        // State
        isLoading,
        isMangaReady,
        // Actions
        fetchMangaData,
        refreshMangaData,
        updateMangaDataDirect,
        buildGroupsFromData,
    };

    return (
        <MangaListContext.Provider value={value}>
            {children}
        </MangaListContext.Provider>
    );
};

export const useMangaList = () => {
    const context = useContext(MangaListContext);
    if (!context) {
        throw new Error('useMangaList must be used within a MangaListProvider');
    }
    return context;
};

export default MangaListContext;
